import React, { useState, useEffect } from 'react';
import { ArrowLeft, ExternalLink, Calendar, MapPin, Search, Filter } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import axios from 'axios';

const Realisations = () => {
  const [filterSecteur, setFilterSecteur] = useState('Tous');
  const [searchTerm, setSearchTerm] = useState('');
  const [realisations, setRealisations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;
    const controller = new AbortController();

    const fetchRealisations = async () => {
      try {
        const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/realisations`);
        if (isMounted) {
          console.log("Fetched data length:", res.data.length);
          const sorted = res.data.sort((a,b) => {
            if (a.reference < b.reference) return -1;
            if (a.reference > b.reference) return 1;
            return 0;
          });
          setRealisations(sorted);
        }
      } catch (e) {
        if (e.name !== 'CanceledError') {
          console.error('Error fetching realisations', e);
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    fetchRealisations();
    return () => { isMounted = false; controller.abort(); };
  }, []);

  const filteredData = realisations.filter(item => {
    // Check filter
    const matchesSearch = item.titre?.toLowerCase().includes(searchTerm.toLowerCase()) || 
                          item.lieu?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesSecteur = filterSecteur === 'Tous' || item.secteur === filterSecteur;
    return matchesSearch && matchesSecteur;
  });

  const secteurs = ['Tous', ...new Set(realisations.map(item => item.secteur))];

  return (
    <div className="bg-slate-50 min-h-screen">
      {/* Header */}
      <div className="bg-[#0A192F] text-white py-12 px-4 border-b border-slate-800">
        <div className="container mx-auto max-w-6xl">
          <div className="flex items-center gap-2 mb-6">
            <Link to="/" className="text-slate-400 hover:text-white transition-colors flex items-center gap-1 text-sm font-medium">
              <ArrowLeft className="w-4 h-4" /> Retour à l'accueil
            </Link>
          </div>
          <h1 className="text-3xl sm:text-4xl font-extrabold mb-4">Médiathèque des Projets</h1>
          <p className="text-blue-200 text-lg max-w-3xl leading-relaxed">
            Archives documentaires des réalisations et chantiers au Lualaba.
          </p>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="container mx-auto max-w-6xl px-4 py-8">
        <div className="bg-white p-4 rounded-xl shadow-sm border border-slate-200 flex flex-col md:flex-row gap-4 justify-between items-center mb-10">
          <div className="relative w-full md:w-96 shrink-0">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <input 
              type="text" 
              placeholder="Rechercher un projet, une ville..." 
              className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-900 bg-slate-50 text-sm"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          
          <div className="flex flex-wrap items-center gap-2 w-full md:w-auto mt-4 md:mt-0">
            <Filter className="w-5 h-5 text-slate-400 mr-2 shrink-0 hidden md:block" />
            {secteurs.map(secteur => (
              <button
                key={secteur}
                onClick={() => setFilterSecteur(secteur)}
                className={`whitespace-nowrap px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${
                  filterSecteur === secteur 
                  ? 'bg-blue-900 text-white shadow-md' 
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                {secteur}
              </button>
            ))}
          </div>
        </div>

        {/* Projects Grid */}
        {loading ? (
          <div className="text-center py-20 bg-white rounded-2xl border border-slate-200">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-900 mx-auto"></div>
            <p className="text-slate-500 mt-4">Chargement du catalogue...</p>
          </div>
        ) : filteredData.length === 0 ? (
          <div className="text-center py-20 bg-white rounded-2xl border border-slate-200">
            <h3 className="text-xl font-bold text-slate-900 mb-2">Aucun projet trouvé</h3>
            <p className="text-slate-500">Modifiez vos filtres de recherche.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredData.map(projet => (
              <div key={projet.id} className="bg-white rounded-2xl overflow-hidden shadow-sm border border-slate-200 hover:shadow-md transition-all duration-300 group flex flex-col">
                <div className="aspect-[4/3] relative overflow-hidden">
                  <div className="absolute inset-0 bg-[#0A192F]/10 group-hover:bg-transparent transition-colors duration-500 z-10"></div>
                  <img 
                    src={projet.image ? (projet.image.startsWith('http') ? projet.image : (projet.image.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${projet.image}` : (projet.image.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${projet.image}` : `${process.env.REACT_APP_BACKEND_URL}/api/files/${projet.image}`))) : "https://images.unsplash.com/photo-1577896851231-70ef18881754?q=80&w=600&auto=format&fit=crop"}
                    onError={(e) => { 
                      console.log("Image load error for", projet.titre, projet.image);
                      e.target.src = "https://images.unsplash.com/photo-1577896851231-70ef18881754?q=80&w=600&auto=format&fit=crop"; 
                    }}
                    alt={projet.titre} 
                    className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-700"
                  />
                  <div className="absolute top-4 left-4 z-20">
                    <span className="bg-white/95 backdrop-blur px-3 py-1.5 rounded-md text-xs font-bold text-[#0A192F] shadow-sm border border-slate-100">
                      {projet.secteur}
                    </span>
                  </div>
                  <div className="absolute top-4 right-4 z-20">
                    <span className={`px-3 py-1.5 rounded-md text-xs font-bold shadow-sm border ${
                      projet.statut === 'Inauguré' || projet.statut === 'Achevé' 
                        ? 'bg-emerald-500 text-white border-emerald-600' 
                        : 'bg-slate-100 text-slate-800 border-slate-200'
                    }`}>
                      {projet.statut}
                    </span>
                  </div>
                </div>
                
                <div className="p-6 flex flex-col flex-grow">
                  <h3 className="text-xl font-bold text-slate-900 mb-3 leading-snug group-hover:text-[#0A192F] transition-colors">
                    {projet.titre}
                  </h3>
                  
                  <div className="flex items-center gap-4 text-xs font-semibold text-slate-500 mb-4">
                    <div className="flex items-center gap-1.5">
                      <Calendar className="w-4 h-4 text-slate-400" /> {projet.date_realisation}
                    </div>
                    <div className="flex items-center gap-1.5">
                      <MapPin className="w-4 h-4 text-slate-400" /> {projet.lieu}
                    </div>
                  </div>
                  
                  <p className="text-slate-600 text-sm leading-relaxed mb-6 line-clamp-3">
                    {projet.description}
                  </p>
                  
                  <div className="mt-auto pt-4 border-t border-slate-100 flex justify-between items-center">
                    <span className="text-xs font-bold text-slate-400">Réf: {projet.reference}</span>
                    <Link to={`/realisations/${projet.id}`}>
                      <Button variant="ghost" className="text-blue-700 hover:text-blue-900 hover:bg-blue-50 font-bold p-0 h-auto">
                        Détails <ExternalLink className="w-4 h-4 ml-1.5" />
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Realisations;
